import ctl from '@netlify/classnames-template-literals'
import { createStore, Provider as JotaiProvider, useAtomValue, useSetAtom } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'
import { Toast } from 'radix-ui'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../button'
import { subscribeUnhandledError } from './bus'
import { appendToastAtom, dismissAllToastsAtom, dismissToastAtom, ToastMessage, toastMessagesAtom } from './store'

const ToastStoreContext = React.createContext<ReturnType<typeof createStore> | null>(null)

export function useToastStoreContext() {
	const ctx = React.useContext(ToastStoreContext)
	if (!ctx) { throw new Error('useToastStoreContext must be used within ToastProvider') }
	return ctx
}

function ToastsRenderer() {
	const { t } = useTranslation('common')
	const messages = useAtomValue(toastMessagesAtom)
	const dismiss = useSetAtom(dismissToastAtom)
	const reversedMessages = React.useMemo(() => [...messages].reverse(), [messages])

	return (
		<AnimatePresence mode="popLayout">
			{reversedMessages.map((m: ToastMessage) => (
				<Toast.Root
					asChild
					key={m.id}
					defaultOpen
					onOpenChange={(open: boolean) => {
						if (!open) { dismiss(m.id) }
					}}
				>
					<motion.div
						layoutId={`toast-${m.id}`}
						initial={{
							opacity: 0,
							translateY: -10,
							rotate: 15,
							transformOrigin: '120% 50%',
						}}
						animate={{
							opacity: 1,
							translateY: 0,
							rotate: 0,
							transformOrigin: '200% 50%',
						}}
						transition={{
							duration: 0.3,
						}}
						exit={{
							opacity: 0,
							translateX: 50,
							filter: 'blur(3px)',
						}}
						className={ctl(`
							group relative rounded-md 
							border border-white ring-1 ring-grey-100 bg-white/80 backdrop-blur-sm 
							px-3 py-2 shadow-md
						`)}
					>
						<div className="flex items-start gap-2">
							<div className="flex min-w-0 flex-1 flex-col">
								<Toast.Title className="truncate text-sm font-medium text-neutral-900">
									{m.title}
								</Toast.Title>
								{m.description
									? (
										<Toast.Description className="mt-0.5 text-xs text-neutral-600">
											{m.description}
										</Toast.Description>
									)
									: null}
							</div>
							<Toast.Close asChild>
								<button
									aria-label={t('common.dismiss')}
									className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-neutral-100"
								>
									<span className="sr-only">{t('common.dismiss')}</span>
									<svg
										width="14"
										height="14"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<line x1="18" y1="6" x2="6" y2="18"></line>
										<line x1="6" y1="6" x2="18" y2="18"></line>
									</svg>
								</button>
							</Toast.Close>
						</div>
					</motion.div>
				</Toast.Root>
			))}
		</AnimatePresence>
	)
}

function DismissAllButton() {
	const { t } = useTranslation('common')
	const messages = useAtomValue(toastMessagesAtom)
	const dismissAll = useSetAtom(dismissAllToastsAtom)

	if (messages.length <= 1) { return null }

	return (
		<motion.div
			layout
			initial={{ opacity: 0, y: -20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -20 }}
			transition={{ duration: 0.3 }}
			className="mt-2 flex justify-center"
		>
			<Button size="tiny" type="subtle" onClick={() => dismissAll()}>
				{t('common.dismissAll')}
			</Button>
		</motion.div>
	)
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
	const store = React.useMemo(() => createStore(), [])

	React.useEffect(() => {
		const unsubscribe = subscribeUnhandledError((event) => {
			store.set(appendToastAtom, {
				title: event.title,
				description: event.description,
				variant: 'error',
				cause: event.cause,
			})
		})
		return () => {
			unsubscribe()
		}
	}, [store])

	return (
		<ToastStoreContext.Provider value={store}>
			<JotaiProvider store={store}>
				<Toast.Provider
					duration={Number.POSITIVE_INFINITY}
					swipeDirection="right"
					swipeThreshold={500}
				>
					{children}
					{/* Fixed top-right viewport with our toasts */}
					<div className="fixed top-2 right-2 z-[100] flex w-[min(100vw,380px)] flex-col">
						<Toast.Viewport className="relative z-[100] m-0 flex max-h-[100vh] w-full list-none flex-col-reverse gap-2 outline-none" />
						<AnimatePresence mode="popLayout">
							<DismissAllButton />
						</AnimatePresence>
					</div>
					<div className="">
						<ToastsRenderer />
					</div>
				</Toast.Provider>
			</JotaiProvider>
		</ToastStoreContext.Provider>
	)
}
